import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Mail, Phone, Linkedin, Download, Building2 } from "lucide-react";
import { MBTI_NAMES_PT, MBTI_TYPE_GROUP, VIA_TRANSLATIONS_PT } from "../members/profileConstants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "Diplomatas" (group label, plural) → "Diplomata" (one person)
const GROUP_SINGULAR = {
  analistas: "Analista",
  diplomatas: "Diplomata",
  sentinelas: "Sentinela",
  exploradores: "Explorador",
};

// "ENFP" → "ENFP · Ativista · Diplomata"
const mbtiFull = (t) => [t, MBTI_NAMES_PT[t], GROUP_SINGULAR[MBTI_TYPE_GROUP[t]?.key]]
  .filter(Boolean).join(" · ");

// Public digital business card page: /cartao/:slug
export default function BusinessCard() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/card/${slug}`);
        setCard(r.data);
        setState("ok");
      } catch { setState("notfound"); }
    })();
  }, [slug]);

  if (state === "loading") {
    return <div className="min-h-screen bg-[hsl(var(--secondary))] flex items-center justify-center text-sm text-[hsl(var(--muted-foreground))]">A carregar…</div>;
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-[hsl(var(--secondary))] flex items-center justify-center p-6" data-testid="card-not-found">
        <div className="text-center">
          <div className="overline">CIEES Portugal</div>
          <h1 className="display-heading text-3xl mt-3">Cartão não disponível.</h1>
          <p className="mt-3 text-sm text-[hsl(var(--muted-foreground))]">Este cartão de visita foi desativado ou não existe.</p>
          <Link to="/" className="mt-6 inline-block text-xs uppercase tracking-[0.2em] text-[hsl(var(--primary))] underline">ciees.pt</Link>
        </div>
      </div>
    );
  }

  const initials = (card.name || "?").trim().split(/\s+/).slice(0, 2).map((s) => s[0] || "").join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[hsl(var(--primary))] flex items-center justify-center p-6" data-testid="business-card-page">
      <div className="w-full max-w-md bg-white border border-[hsl(var(--border))] shadow-2xl">
        {/* Header strip */}
        <div className="bg-[hsl(var(--primary))] text-white px-8 pt-8 pb-16 relative">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--accent))]">CIEES Portugal{card.tier_label ? ` · ${card.tier_label}` : ""}</div>
        </div>
        {/* Photo overlapping — relative z-10 so it paints ABOVE the (positioned) header strip */}
        <div className="px-8 -mt-12 relative z-10">
          {card.picture ? (
            <img src={card.picture} alt={card.name} className="h-24 w-24 rounded-full object-cover object-top border-4 border-white shadow-lg" data-testid="card-photo" />
          ) : (
            <div className="h-24 w-24 rounded-full bg-[hsl(var(--secondary))] border-4 border-white shadow-lg flex items-center justify-center text-2xl font-medium text-[hsl(var(--primary))]">{initials}</div>
          )}
        </div>
        <div className="px-8 pt-4 pb-8">
          <h1 className="display-heading text-3xl leading-tight" data-testid="card-name">{card.name}</h1>
          {(card.role || card.company) && (
            <p className="mt-1.5 text-sm text-[hsl(var(--muted-foreground))] flex items-center gap-1.5" data-testid="card-role-company">
              <Building2 className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
              {[card.role, card.company].filter(Boolean).join(" · ")}
            </p>
          )}

          <div className="mt-6 space-y-3">
            {card.email && (
              <a href={`mailto:${card.email}`} className="flex items-center gap-3 text-sm text-[hsl(var(--foreground))] hover:text-[hsl(var(--primary))]" data-testid="card-email">
                <Mail className="h-4 w-4 text-[hsl(var(--accent))]" /> {card.email}
              </a>
            )}
            {card.phone && (
              <a href={`tel:${card.phone}`} className="flex items-center gap-3 text-sm text-[hsl(var(--foreground))] hover:text-[hsl(var(--primary))]" data-testid="card-phone">
                <Phone className="h-4 w-4 text-[hsl(var(--accent))]" /> {card.phone}
              </a>
            )}
            {card.linkedin && (
              <a href={card.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm text-[#0A66C2] hover:underline" data-testid="card-linkedin">
                <Linkedin className="h-4 w-4" /> LinkedIn
              </a>
            )}
          </div>

          {(card.mbti || (card.via_values || []).length > 0) && (
            <div className="mt-6 pt-5 border-t border-[hsl(var(--border))] flex flex-wrap gap-1.5" data-testid="card-badges">
              {card.mbti && (
                <span className="px-2.5 py-1 text-[11px] tracking-wide bg-[hsl(var(--primary))] text-white" data-testid="card-mbti">{mbtiFull(card.mbti)}</span>
              )}
              {(card.via_values || []).map((v) => (
                <span key={v} className="px-2.5 py-1 text-[11px] bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))]">{VIA_TRANSLATIONS_PT[v] || v}</span>
              ))}
            </div>
          )}

          <a
            href={`${API}/card/${slug}/vcard`}
            className="mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[hsl(var(--accent))] text-[#1A201F] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#B3922E] transition-colors"
            data-testid="card-save-contact"
          >
            <Download className="h-4 w-4" /> Guardar contacto
          </a>

          <p className="mt-6 text-center text-[11px] text-[hsl(var(--muted-foreground))] leading-relaxed">
            Sócio da <a href="https://ciees.pt" className="text-[hsl(var(--primary))] underline">CIEES Portugal</a><br />
            Comunidade de Empresários e Executivos de Schoenstatt
          </p>
        </div>
      </div>
    </div>
  );
}
